import { useState, Fragment, useEffect } from "react";
import { Box, Typography, Avatar, Grid, Button, IconButton, Stack, Divider, CircularProgress } from "@mui/material";
import { styled } from "@mui/system";
import ClearIcon from "@mui/icons-material/Clear";
import ProfileTabs from "../common/ProfileTabs";
import { ROUTE_VIDEO_COMPLETE } from "../../constants/routes";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { deleteUserVideo, getUserVideos, saveUserVideos, setErrorMessage } from "../../redux/actions/appActions";
import { GET_PRESIGNED_VIDEO_URL } from "../../redux/api/config";
import { ACCESS_TOKEN } from "../../constants/AppVarConstants";
import { Cancel } from "@mui/icons-material";
import { SET_PROMPTS } from "../../redux/actions/actionTypes";
import SeeviHeader from "../common/SeeviHeader";
import VideoReview from "../profile/VideoReview";
import { RootState } from "../../redux/types/reduxState";

const UploadButton = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "8px 40px",
  fontSize: "16px",
  fontWeight: "600",
  borderRadius: "10px",
  border: "1px dashed #756DE9",
  backgroundColor: "#f5f5f5",
  color: "#756DE9",
  textTransform: "none",
  [theme.breakpoints.down("sm")]: {
    padding: "8px 40px",
  },
}));

const StyledTextBox1 = styled(Box)({
  fontSize: "16px",
  backgroundColor: "#ffffff",
  width: "303px",
  borderRadius: "10px",
  paddingTop: "16px",
  fontFamily: "Poppins, sans-serif",
  fontWeight: "400",
  color: "#1F2235",
  lineHeight: "1.5",
  marginLeft: "10px",
  paddingBottom: "10px",
});

const UploadedFileBox = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  backgroundColor: "#F5F6FA",
  // padding: "15px",
  borderRadius: "10px",
});

const UploadFinalVideo = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const user = useSelector((state: any) => state.app.user);
  const userVideos = useSelector((state: RootState) => state.app.userVideos);

  const { profileStatus } = user?.user || {};
  const { videoUploaded, videoStatus } = profileStatus || {};
  const underReview = videoStatus === "INACTIVE" && videoUploaded;
  const prompts = useSelector((state: any) => state.app.prompts);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<any>({});
  const [, setUploadedVideoUrls] = useState<any[]>([]);

  useEffect(() => {
    if (location?.search?.includes("edit")) {
      dispatch(getUserVideos(true, setSelectedFiles));
    }
  }, []);

  const getPreSignedUrl = async (fileName: string) => {
    try {
      const accessToken = localStorage.getItem(ACCESS_TOKEN);
      const response = await fetch(`${GET_PRESIGNED_VIDEO_URL}?filePath=${fileName}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      const data = await response.json();
      return data.result; // Pre-signed URL for file upload
    } catch (error) {
      console.error("Error getting pre-signed URL:", error);
    }
  };

  const handleFileSelect = (event: any, key: any) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check if file size exceeds 100MB (150 * 1024 * 1024 bytes)
    const maxSizeInBytes = 150 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      dispatch(setErrorMessage("Video size must be less than 100MB. Please upload a smaller file."));
      return;
    }

    setSelectedFiles((prevState: any) => ({
      ...prevState,
      [key]: file,
    }));
  };

  const handleRemoveFile = (key: any) => {
    setSelectedFiles((prevState: any) => ({
      ...prevState,
      [key]: null,
    }));
  };

  const handleDeleteVideo = (questionId: any) => {
    dispatch(deleteUserVideo(questionId));
  };

  const handleUpload = async () => {
    setLoading(true);

    const allVideosUploaded = Object.keys(prompts).every((key) => selectedFiles[key]);

    if (!allVideosUploaded) {
      setLoading(false);
      dispatch(setErrorMessage("All videos are required."));
      return;
    }

    const keys = Object.keys(prompts);

    try {
      // Step 1: Get all pre-signed URLs in parallel
      const preSignedUrlPromises = keys.map(async (key) => {
        const file = selectedFiles[key];
        if (file) {
          const preSignedUrl = await getPreSignedUrl(file.name);
          return { key, file, preSignedUrl };
        }
        return { key, file: null, preSignedUrl: null };
      });

      const preSignedUrlResults = await Promise.all(preSignedUrlPromises);

      // Check if any pre-signed URL failed
      const failedUrl = preSignedUrlResults.find((result) => result.file && !result.preSignedUrl);
      if (failedUrl) {
        setLoading(false);
        dispatch(setErrorMessage("Error getting pre-signed URL"));
        return;
      }

      // Step 2: Upload all files in parallel
      const uploadPromises = preSignedUrlResults
        .filter((result) => result.file && result.preSignedUrl)
        .map(async ({ key, file, preSignedUrl }) => {
          await fetch(preSignedUrl!, {
            method: "PUT",
            body: file,
          });

          const fileUrl = preSignedUrl!.split("?")[0]; // Store the file's accessible URL
          setUploadedVideoUrls((prevState) => [...prevState, fileUrl]);

          return {
            questionId: prompts[key]?.questionId,
            videoPath: fileUrl,
            videoPrivacy: "PUBLIC",
          };
        });

      const payload = await Promise.all(uploadPromises);

      dispatch(saveUserVideos(payload, onSuccess, onError));
    } catch (error) {
      console.error("Error uploading file:", error);
      dispatch(setErrorMessage("Error uploading file"));
      setLoading(false);
    }
  };

  const onSuccess = () => {
    setLoading(false);
    if (location?.search?.includes("edit")) {
      navigate(`${ROUTE_VIDEO_COMPLETE}?edit`);
    } else {
      navigate(`${ROUTE_VIDEO_COMPLETE}`);
    }
  };

  const onError = () => {
    setLoading(false);
  };

  const handleRemoveVideo = (promptKey: any, questionId: any) => {
    const allPrompts = { ...prompts };
    delete allPrompts[promptKey];
    dispatch({ type: SET_PROMPTS, payload: allPrompts });
    handleDeleteVideo(questionId);
  };

  return (
    <Box display="flex" flexDirection="column" justifyContent="center" bgcolor="#eeeeee" width="100%">
      <SeeviHeader />
      <ProfileTabs />

      <Box sx={{ overflowX: "auto" }}>{/* Tabs are removed as they are not used */}</Box>

      <Box display="flex" flexDirection="column" alignItems="left" fontSize="14px" color="#757575">
        <Box display="flex" alignItems="center" sx={{ backgroundColor: "#eeeeee", padding: "5px 16px" }}>
          <Avatar
            // component="img"
            src="/images/avatar-one.png"
            alt="Avatar"
            sx={{
              width: "51px",
              height: "51px",
            }}
          />
          <StyledTextBox1 px={1} mt={2}>
            <Box px={1}>
              <Typography className="custom-typography-video">3/3</Typography>
              <Typography className="custom-typography-video">Let&apos;s upload your final videos</Typography>
            </Box>
          </StyledTextBox1>
        </Box>

        {/* <Box display="flex" alignItems="center" p={2} sx={{ backgroundColor: "#eeeeee", padding: "5px 16px" }}>
          <StyledTextBox1 px={2}>
            Tip: Your recorded video will be in your gallery. Click on “Browse To Upload” to continue
          </StyledTextBox1>
        </Box>

        <Box display="flex" alignItems="center" p={2} sx={{ backgroundColor: "#eeeeee", padding: "5px 16px" }}>
          <StyledTextBox1 px={2} mb={2}>
            If you have not recorded yet, Please go to your mobile camera to record and upload.
          </StyledTextBox1>
        </Box> */}
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        p={2}
        pt={4}
        bgcolor="#ffffff"
        borderRadius="25px 25px 0 0"
        marginTop="16px"
        boxShadow="0 4px 12px rgba(0,0,0,0.1)"
      >
        {!underReview ? (
          <Grid container spacing={2}>
            {Object.keys(prompts).map((key, index) => (
              <Fragment key={index}>
                <Grid item xs={12} sm={4}>
                  <Stack spacing={1} mb={2}>
                    <Typography sx={{ fontSize: "1.25rem", fontWeight: "600" }} className="custom-typography-video">
                      Video of &quot;{key}&quot;
                    </Typography>
                    <Typography className="custom-typography">{prompts[key]?.question}</Typography>
                  </Stack>
                  <UploadButton>
                    <Button component="label" sx={{ textTransform: "none", fontSize: "16px" }}>
                      {selectedFiles[key] ? (
                        <UploadedFileBox>
                          <Typography
                            sx={{
                              color: "#756DE9",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "350px",
                            }}
                          >
                            {selectedFiles[key].name}
                          </Typography>
                          <IconButton
                            sx={{ padding: 0, ml: 1 }}
                            onClick={() => handleRemoveFile(key)}
                            aria-label="remove"
                          >
                            <ClearIcon sx={{ color: "#756DE9" }} />
                          </IconButton>
                        </UploadedFileBox>
                      ) : (
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography style={{ color: "#756DE9" }}>Browse to Upload</Typography>
                          <img src="images/upload-line.png" width="22px" height="22px" style={{ marginLeft: "10px" }} />
                        </Box>
                      )}
                      <input hidden type="file" capture={false} onChange={(e) => handleFileSelect(e, key)} />
                    </Button>
                  </UploadButton>
                  {location?.search?.includes("edit") ? (
                    <Box
                      onClick={() => handleRemoveVideo(key, prompts[key]?.questionId)}
                      sx={{ display: "flex", alignItems: "center", mt: 1 }}
                    >
                      <Cancel className="custom-typography" sx={{ mr: 0.5, fontSize: "14px" }} />
                      <Typography className="custom-typography">Remove Video</Typography>
                    </Box>
                  ) : null}
                </Grid>

                {Object.keys(prompts)?.length - 1 === index ? null : (
                  <Grid item xs={12}>
                    <Divider orientation="vertical" flexItem sx={{ mt: 1, borderWidth: "1px", height: "auto" }} />
                  </Grid>
                )}
              </Fragment>
            ))}
          </Grid>
        ) : (
          <VideoReview sections={userVideos} />
        )}
      </Box>
      <Box display="flex" flexDirection="column" p={2} bgcolor="#ffffff" sx={{ borderRadius: "0px" }}>
        <Box display="flex">
          <Button
            sx={{
              backgroundColor: "white",
              borderRadius: "99px",
              fontSize: "16px",
              textTransform: "capitalize",
              width: "25%",
              color: "#000000",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
              flexGrow: 1,
            }}
            variant="contained"
            size="small"
            style={{ padding: "15px 40px" }}
            className="w-100 mt-2 btn-pad"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Box flexGrow={1}></Box>
          {underReview ? null : location?.search?.includes("edit") ? (
            <Button
              sx={{
                backgroundColor: "#756DE9",
                borderRadius: "99px",
                fontSize: "16px",
                textTransform: "capitalize",
                width: "70%",
                paddingY: "15px",
                paddingX: 0,
                "&:hover": {
                  backgroundColor: "#756DE9",
                },
              }}
              endIcon={loading ? <CircularProgress size="1.2rem" sx={{ color: "#fff" }} /> : null}
              variant="contained"
              size="small"
              className="w-100 mt-2 btn-pad"
              disabled={loading}
              onClick={handleUpload}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          ) : (
            <Button
              sx={{
                backgroundColor: "#756DE9",
                borderRadius: "99px",
                fontSize: "16px",
                textTransform: "capitalize",
                width: "70%",
                paddingY: "15px",
                paddingX: 0,
                "&:hover": {
                  backgroundColor: "#756DE9",
                },
              }}
              endIcon={loading ? <CircularProgress size="1.2rem" sx={{ color: "#fff" }} /> : null}
              variant="contained"
              size="small"
              className="w-100 mt-2 btn-pad"
              disabled={loading}
              onClick={handleUpload}
            >
              {loading ? "Uploading..." : "Upload"}
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default UploadFinalVideo;
